import React from 'react';
import { FaCalendarCheck, FaUserCheck, FaUserTimes, FaChalkboardTeacher, FaChartLine, FaExclamationTriangle } from 'react-icons/fa';
import { ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, PieChart, Pie, Cell } from 'recharts';

export default function DashboardAttendance({
  todayAttendance, teacherAttendance, attendanceTrend, attendanceTrendDays, handleAttendanceTrendRangeChange, lowAttendanceStudents, formatCurrency
}) {
  return (
    <div className="dashboard-content">
      {/* Attendance Metrics */}
      <div className="metrics-grid">
        <div className="metric-card attendance">
          <div className="metric-icon"><FaCalendarCheck /></div>
          <div className="metric-info">
            <p className="metric-label">Today's Student Attendance</p>
            <h2 className="metric-value">{todayAttendance?.attendanceRate || 0}%</h2>
            <p className="metric-subtext">{todayAttendance?.presentCount || 0} / {todayAttendance?.totalStudents || 0}</p>
          </div>
        </div>
        <div className="metric-card success">
          <div className="metric-icon"><FaUserCheck /></div>
          <div className="metric-info">
            <p className="metric-label">Students Present</p>
            <h2 className="metric-value">{todayAttendance?.presentCount || 0}</h2>
          </div>
        </div>
        <div className="metric-card warning">
          <div className="metric-icon"><FaUserTimes /></div>
          <div className="metric-info">
            <p className="metric-label">Students Absent</p>
            <h2 className="metric-value">{todayAttendance?.absentCount || 0}</h2>
          </div>
        </div>
        <div className="metric-card info">
          <div className="metric-icon"><FaChalkboardTeacher /></div>
          <div className="metric-info">
            <p className="metric-label">Teacher Attendance</p>
            <h2 className="metric-value">{teacherAttendance?.attendanceRate || 0}%</h2>
            <p className="metric-subtext">{teacherAttendance?.presentCount || 0} / {teacherAttendance?.totalTeachers || 0}</p>
          </div>
        </div>
      </div>
      {/* Attendance Charts */}
      <div className="charts-row">
        <div className="chart-card large">
          <div className="chart-header">
            <h3><FaChartLine /> Attendance Trend</h3>
            <div className="chart-filters">
              <select 
                value={attendanceTrendDays} 
                onChange={e => handleAttendanceTrendRangeChange(parseInt(e.target.value))}
                className="trend-filter"
              >
                <option value="7">Last 7 Days</option>
                <option value="30">Last Month (30 Days)</option>
                <option value="180">Last 6 Months</option>
                <option value="365">Last Year</option>
              </select>
            </div>
          </div>
          <div className="chart-body">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={attendanceTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ecf0f1" />
                <XAxis 
                  dataKey="date" 
                  stroke="#7f8c8d" 
                  angle={attendanceTrendDays > 30 ? -45 : 0}
                  textAnchor={attendanceTrendDays > 30 ? "end" : "middle"}
                  height={attendanceTrendDays > 30 ? 80 : 60}
                />
                <YAxis stroke="#7f8c8d" domain={[0, 100]} />
                <Tooltip formatter={value => `${value}%`} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="rate" 
                  stroke="#2ecc71" 
                  strokeWidth={3}
                  dot={attendanceTrendDays <= 30 ? { fill: '#2ecc71', r: 6 } : false}
                  name="Attendance Rate (%)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="chart-card">
          <div className="chart-header">
            <h3><FaUserCheck /> Today's Breakdown</h3>
          </div>
          <div className="chart-body">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Present', value: todayAttendance?.presentCount || 0 },
                    { name: 'Absent', value: todayAttendance?.absentCount || 0 }
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  <Cell fill="#2ecc71" />
                  <Cell fill="#e74c3c" />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* Low Attendance Students */}
      <div className="widgets-row">
        <div className="widget-card full-width">
          <div className="widget-header">
            <h3><FaExclamationTriangle /> Students with Low Attendance (&lt;75%)</h3>
          </div>
          <div className="widget-body">
            {lowAttendanceStudents.length > 0 ? (
              <div className="low-attendance-table">
                <table>
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Name</th>
                      <th>Roll No</th>
                      <th>Class</th>
                      <th>Present Days</th>
                      <th>Total Days</th>
                      <th>Attendance %</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lowAttendanceStudents.map((student, index) => (
                      <tr key={student.rno}>
                        <td>#{index + 1}</td>
                        <td>{student.name}</td>
                        <td>{student.rno}</td>
                        <td><span className="class-badge">{student.class}</span></td>
                        <td>{student.presentDays}</td>
                        <td>{student.totalDays}</td>
                        <td>
                          <span className={`attendance-badge ${
                            parseFloat(student.attendancePercentage) < 50 ? 'critical' :
                            parseFloat(student.attendancePercentage) < 65 ? 'danger' : 'warning'
                          }`}>
                            {student.attendancePercentage}%
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${
                            parseFloat(student.attendancePercentage) < 50 ? 'critical' : 'warning'
                          }`}>
                            {parseFloat(student.attendancePercentage) < 50 ? 'Critical' : 'Needs Attention'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="no-data">All students have good attendance! 🎉</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
